import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography, Alert } from "@mui/material";
import { useImpersonation } from "../actprep-ui/context/ImpersonationContext";
import { useProfile } from "../actprep-ui/context/ProfileContext";
import { urls, apiUrls } from "../actprep-ui/utils/urls";
import useGeneralAPI from "../actprep-ui/hooks/api/useGeneralApi";
import { useGet } from "../actprep-ui/hooks/api";
import { apiOrigin } from "../actprep-ui/constants/api";
import { groups } from "../actprep-ui/constants/auth";

function buildStudentImpersonation(studentData) {
  const firstName =
    studentData.user?.first_name ||
    studentData.user__first_name ||
    studentData.first_name;
  const lastName =
    studentData.user?.last_name ||
    studentData.user__last_name ||
    studentData.last_name;
  const email =
    studentData.user?.email || studentData.user__email || studentData.email;
  const fallbackLabel = `${firstName || ""} ${lastName || ""}`.trim();

  return {
    uuid: studentData.uuid,
    label: studentData.__str__ || fallbackLabel || email || "Unknown Student",
    data: {
      user_profile_id:
        studentData.user?.profile?.uuid ||
        studentData.user__profile__uuid ||
        studentData.user_profile_id,
      enrollment_completed: studentData.enrollment_completed,
      user: {
        first_name: firstName || "",
        last_name: lastName || "",
        email: email || "",
      },
    },
  };
}

export default function ImpersonateStudent() {
  const { studentUuid } = useParams();
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { setImpersonation, clearImpersonation } = useImpersonation();
  const [error, setError] = useState(null);

  const isEmployee = profile?.groups?.includes(groups.EMPLOYEES);
  const isParent = profile?.groups?.includes(groups.PARENTS) && !isEmployee;
  const parentProfileUuid = profile?.parent?.uuid;
  const canAccess = isEmployee || isParent;

  // Drop any prior target immediately so a same-tab switch cannot keep showing
  // the previous student while this page loads the new one.
  useEffect(() => {
    clearImpersonation();
  }, [studentUuid, clearImpersonation]);

  const {
    data: studentResponse,
    isLoading: isLoadingEmployeeStudent,
    isError: isEmployeeStudentError,
  } = useGeneralAPI(
    "StudentProfile",
    {
      filter: { uuid: studentUuid },
      values: [
        "uuid",
        "user__profile__uuid",
        "user__first_name",
        "user__last_name",
        "user__email",
        "first_name",
        "last_name",
        "email",
        "enrollment_completed",
      ],
      keepRelated: true,
      selectRelated: ["user", "user__profile"],
      customSerializer: true,
    },
    {
      enabled: !!studentUuid && !!profile?.uuid && isEmployee,
    }
  );

  const {
    data: parentStudentsData,
    isLoading: isLoadingParentStudents,
    isError: isParentStudentsError,
  } = useGet(parentProfileUuid ? apiUrls.parent(parentProfileUuid).students() : null, {
    enabled: !!studentUuid && !!profile?.uuid && isParent && !!parentProfileUuid,
    params: {},
  });

  useEffect(() => {
    if (profile?.uuid && !canAccess) {
      setError("Access Denied.");
      setTimeout(() => navigate(urls.error(403)), 2000);
      return;
    }

    if (isEmployee && studentResponse?.data?.length > 0) {
      const studentData = studentResponse.data[0];

      if (studentData.enrollment_completed === false) {
        window.location.href = `${apiOrigin}/enrollment/student/`;
        return;
      }

      setImpersonation(buildStudentImpersonation(studentData));
      navigate(urls.students.internal.dashboard());
      return;
    }

    if (isParent && parentStudentsData?.data?.students) {
      const studentData = parentStudentsData.data.students.find(
        (student) => student.uuid === studentUuid
      );

      if (!studentData) {
        setError("This student is not linked to your parent account.");
        setTimeout(() => navigate(urls.error(403)), 2000);
        return;
      }

      if (studentData.enrollment_completed === false) {
        setError("This student has not completed enrollment yet.");
        setTimeout(() => navigate(urls.testing.home.dashboard()), 2000);
        return;
      }

      setImpersonation(buildStudentImpersonation(studentData));
      navigate(urls.students.internal.dashboard());
      return;
    }

    const isLoading = isEmployee
      ? isLoadingEmployeeStudent
      : isLoadingParentStudents;
    const isError = isEmployee ? isEmployeeStudentError : isParentStudentsError;

    if (!isLoading && isEmployee && studentResponse && studentResponse.data?.length === 0) {
      setError("Student not found.");
      setTimeout(() => navigate(urls.testing.home.dashboard()), 2000);
    } else if (!isLoading && isError) {
      setError("Failed to set up student view. Please try again.");
      setTimeout(() => navigate(urls.testing.home.dashboard()), 2000);
    }
  }, [
    studentResponse,
    parentStudentsData,
    isLoadingEmployeeStudent,
    isLoadingParentStudents,
    isEmployeeStudentError,
    isParentStudentsError,
    profile,
    canAccess,
    isEmployee,
    isParent,
    parentProfileUuid,
    setImpersonation,
    navigate,
    studentUuid,
  ]);

  const isLoading = isEmployee ? isLoadingEmployeeStudent : isLoadingParentStudents;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "50vh",
        gap: 2,
        padding: 4,
      }}
    >
      {isLoading && !error && (
        <>
          <CircularProgress />
          <Typography variant="h6" color="text.secondary">
            Opening student dashboard...
          </Typography>
        </>
      )}
      {error && (
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
